// src/admin/pages/PaymentsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  CreditCard, 
  RefreshCw, 
  Search,
  DollarSign,
  Eye,
  X,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Copy,
  ExternalLink,
  Loader2,
  Trash2,
  History
} from 'lucide-react';
import { supabase } from '../../config';
import stripeService from '../../services/stripeService';
import { getUserDisplayName } from '../../services/utilityService';

const PaymentsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('subscriptions'); // 'subscriptions', 'payments', 'drafts', 'subscription-log'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [refreshingInvoice, setRefreshingInvoice] = useState(false);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] = useState(null);
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [finalizingInvoiceId, setFinalizingInvoiceId] = useState(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [subscriptionAuditLogs, setSubscriptionAuditLogs] = useState([]);
  const [changeTypeFilter, setChangeTypeFilter] = useState('all'); // 'all', 'upgrade', 'downgrade', 'cancel', etc.
  const hasInitializedFromUrl = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Helper: normalize stripe_subscription_id to a plain string (avoid sending JSON to API)
      const normalizeSubId = (val) => {
        if (!val) return null;
        if (typeof val === 'string') {
          // If it's a JSON string, try to parse
          if (val.startsWith('{')) {
            try {
              const parsed = JSON.parse(val);
              return parsed?.id || null;
            } catch {
              // fall through
            }
          }
          return val;
        }
        if (typeof val === 'object') {
          return val.id || null;
        }
        return null;
      };

      // Fetch subscriptions
      // Try to fetch subscriptions with join first, but handle missing foreign key gracefully
      let subsData = [];
      let subsError = null;
      
      // Try with join first
      const joinResult = await supabase
        .from('subscriptions')
        .select(`
          *,
          user_profiles (
            id,
            first_name,
            last_name,
            email,
            username
          )
        `)
        .order('created_at', { ascending: false });
      
      // Check if error is specifically about missing foreign key relationship
      if (joinResult.error) {
        const errorCode = joinResult.error.code || joinResult.error.message || '';
        const errorMessage = joinResult.error.message || '';
        
        // Check if it's a foreign key relationship error (PGRST200 or 400 with specific message)
        if (errorCode === 'PGRST200' || errorMessage.includes('foreign key relationship')) {
          // Foreign key relationship doesn't exist - fetch separately (expected behavior)
          subsError = null;
        } else {
          // Real error - return it
          subsData = [];
          subsError = joinResult.error;
        }
      } else {
        // Success with join
        subsData = joinResult.data || [];
        subsError = null;
      }
      
      // If we don't have data yet (either error or missing relationship), fetch without join
      if ((!subsData || subsData.length === 0) && !subsError) {
        const simpleResult = await supabase
          .from('subscriptions')
          .select('*')
          .order('created_at', { ascending: false });
        
        subsData = simpleResult.data || [];
        subsError = simpleResult.error;
        
        // Fetch user profiles separately if we have subscriptions
        if (subsData && subsData.length > 0 && !subsError) {
          const userIds = subsData.map(sub => sub.user_id).filter(Boolean);
          if (userIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, email, username')
              .in('id', userIds);
            
            // Map profiles to subscriptions (even if profiles query fails, we still have subscriptions)
            if (profiles && !profilesError) {
              const profileMap = {};
              profiles.forEach(profile => {
                profileMap[profile.id] = profile;
              });
              subsData = subsData.map(sub => ({
                ...sub,
                user_profiles: profileMap[sub.user_id] || null
              }));
            }
          }
        }
      }

      if (subsError) {
        console.error('Error fetching subscriptions:', subsError);
        setError(`Failed to load subscriptions: ${subsError.message || 'Unknown error'}`);
        setSubscriptions([]);
      } else {
        // Normalize stripe_subscription_id to avoid JSON being sent to cancel API
        const normalizedSubs = (subsData || []).map(sub => ({
          ...sub,
          stripe_subscription_id: normalizeSubId(sub.stripe_subscription_id)
        }));
        setSubscriptions(normalizedSubs);
      }

      // Fetch billing history from Stripe for all customers with subscriptions
      // This ensures we get all subscription invoices, not just ones saved to billing_history table
      const allBillingHistory = [];
      const uniqueCustomerIds = new Set();
      
      // Collect all unique customer IDs from subscriptions
      if (subsData && subsData.length > 0) {
        subsData.forEach(sub => {
          if (sub.stripe_customer_id) {
            uniqueCustomerIds.add(sub.stripe_customer_id);
          }
        });
      }
      
      // Fetch billing history from Stripe for each customer
      for (const customerId of uniqueCustomerIds) {
        try {
          const response = await stripeService.getBillingHistory(customerId);
          if (response.success && response.billing_history) {
            // Add customer info to each invoice
            const customerSub = subsData.find(s => s.stripe_customer_id === customerId);
            response.billing_history.forEach(invoice => {
              // Normalize amount - Stripe API returns 'amount', 'total', 'amount_due', 'amount_paid'
              // but database uses 'amount_cents'
              const amount_cents = invoice.amount_cents || invoice.total || invoice.amount || invoice.amount_due || invoice.amount_paid || 0;
              
              allBillingHistory.push({
                ...invoice,
                amount_cents: amount_cents, // Normalize to amount_cents for consistent display
                invoice_number: invoice.invoice_number || invoice.number || invoice.id, // Normalize invoice number
                customer_id: customerId,
                user_id: customerSub?.user_id,
                user_profiles: customerSub?.user_profiles,
                // Ensure subscription_id is set (might be 'subscription' field from Stripe)
                subscription_id: invoice.subscription_id || invoice.subscription || null
              });
            });
          }
        } catch (err) {
          console.error(`Error fetching billing history for customer ${customerId}:`, err);
          // Continue with other customers even if one fails
        }
      }
      
      // Also fetch from billing_history table as a fallback (for one-off invoices)
      try {
        const { data: billingData, error: billingError } = await supabase
          .from('billing_history')
          .select('*')
          .order('created_at', { ascending: false });

        if (!billingError && billingData) {
          // Add to allBillingHistory, avoiding duplicates by invoice ID
          const existingInvoiceIds = new Set(allBillingHistory.map(b => b.id || b.stripe_invoice_id));
          billingData.forEach(bill => {
            const invoiceId = bill.stripe_invoice_id || bill.id;
            if (invoiceId && !existingInvoiceIds.has(invoiceId)) {
              // Enhance billing_history record with customer info from metadata
              const enhancedBill = {
                ...bill,
                // Normalize amount - ensure amount_cents is set (might be 0 or null)
                amount_cents: bill.amount_cents || 0,
                // Ensure subscription_id is properly set (might be null for one-off invoices)
                subscription_id: bill.subscription_id || null,
                // Extract customer info from metadata if available
                customer: bill.metadata?.customer_name || bill.metadata?.customer_email ? {
                  name: bill.metadata.customer_name,
                  email: bill.metadata.customer_email
                } : undefined
              };
              allBillingHistory.push(enhancedBill);
              existingInvoiceIds.add(invoiceId);
            }
          });
        }
      } catch (err) {
        console.error('Error fetching billing history from database:', err);
        // Continue even if database fetch fails
      }
      
      // Sort by date (most recent first)
      allBillingHistory.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : (a.created_at ? new Date(a.created_at) : new Date(0));
        const dateB = b.date ? new Date(b.date) : (b.created_at ? new Date(b.created_at) : new Date(0));
        return dateB - dateA;
      });
      
      setBillingHistory(allBillingHistory);

      // Fetch draft invoices separately
      if (activeTab === 'drafts') {
        try {
          const draftResponse = await stripeService.listDraftInvoices();
          if (draftResponse.success && draftResponse.invoices) {
            setDraftInvoices(draftResponse.invoices);
          }
        } catch (err) {
          console.error('Error fetching draft invoices:', err);
          // Don't fail the whole fetch if drafts fail
        }
      }

    } catch (error) {
      console.error('Error fetching payment data:', error);
      setError(`Error loading data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Read tab from URL query parameter on mount only, then fetch initial data
  useEffect(() => {
    if (hasInitializedFromUrl.current) return; // Only read URL once on mount
    
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ['subscriptions', 'payments', 'drafts', 'subscription-log'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // If no tab param, keep default 'subscriptions' (initial state)
    hasInitializedFromUrl.current = true;
    
    // Fetch initial data after setting tab from URL
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    // Update URL when tab changes
    const url = new URL(window.location);
    if (activeTab === 'subscriptions') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', activeTab);
    }
    window.history.replaceState({}, '', url);
  }, [activeTab]);

  useEffect(() => {
    // Refresh data when switching tabs (only after URL initialization)
    if (!hasInitializedFromUrl.current) return;
    
    if (activeTab === 'subscriptions' || activeTab === 'payments') {
      fetchData();
    } else if (activeTab === 'drafts') {
      // Fetch drafts when drafts tab is selected
      const fetchDrafts = async () => {
        try {
          const draftResponse = await stripeService.listDraftInvoices();
          if (draftResponse.success && draftResponse.invoices) {
            setDraftInvoices(draftResponse.invoices);
          } else {
            setDraftInvoices([]);
          }
        } catch (err) {
          console.error('Error fetching draft invoices:', err);
          setDraftInvoices([]);
        }
      };
      fetchDrafts();
    } else if (activeTab === 'subscription-log') {
      // Fetch subscription audit logs when subscription-log tab is selected
      const fetchAuditLogs = async () => {
        setLoading(true);
        try {
          // Fetch audit logs
          const { data: logsData, error: logsError } = await supabase
            .from('subscriptions_audit_log')
            .select('*')
            .order('requested_at', { ascending: false })
            .limit(500);
          
          if (logsError) throw logsError;
          
          // Get unique user IDs from logs
          const userIds = [...new Set(logsData.map(log => log.user_id).filter(Boolean))];
          
          // Fetch user profiles separately
          let userProfilesMap = {};
          if (userIds.length > 0) {
            const { data: profilesData, error: profilesError } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, email')
              .in('id', userIds);
            
            if (!profilesError && profilesData) {
              profilesData.forEach(profile => {
                userProfilesMap[profile.id] = profile;
              });
            }
          }
          
          // Merge user profiles with audit logs
          const logsWithProfiles = (logsData || []).map(log => ({
            ...log,
            user_profiles: userProfilesMap[log.user_id] || null
          }));
          
          setSubscriptionAuditLogs(logsWithProfiles);
        } catch (err) {
          console.error('Error fetching subscription audit logs:', err);
          setError(`Failed to load subscription audit logs: ${err.message}`);
          setSubscriptionAuditLogs([]);
        } finally {
          setLoading(false);
        }
      };
      fetchAuditLogs();
    }
  }, [activeTab, fetchData]);


  const formatCurrency = (cents) => {
    if (!cents) return '$0.00';
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status, size = 'normal') => {
    const statusConfig = {
      'active': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Active' },
      'canceled': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Canceled' },
      'past_due': { bg: 'bg-orange-100', text: 'text-orange-800', icon: Clock, label: 'Past Due' },
      'trialing': { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock, label: 'Trialing' },
      'paid': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Paid' },
      'open': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending Payment' }, // Finalized but unpaid invoice
      'draft': { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock, label: 'Draft' }, // Draft invoice
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      'void': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Voided' }, // Voided invoice
      'uncollectible': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Uncollectible' }, // Uncollectible invoice
      'failed': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Failed' }
    };

    const config = statusConfig[status?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock, label: status || 'Unknown' };
    const Icon = config.icon;
    const isLarge = size === 'large';

    return (
      <span className={`inline-flex items-center ${isLarge ? 'px-4 py-2 rounded-lg text-base font-semibold' : 'px-2.5 py-0.5 rounded-full text-xs font-medium'} ${config.bg} ${config.text}`}>
        <Icon className={`${isLarge ? 'h-5 w-5' : 'h-3 w-3'} mr-2`} />
        {config.label}
      </span>
    );
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const searchLower = searchTerm.toLowerCase();
    const userDisplayName = getUserDisplayName(sub.user_profiles || {}, '').toLowerCase();
    const matchesSearch = (
      userDisplayName.includes(searchLower) ||
      sub.user_profiles?.email?.toLowerCase().includes(searchLower) ||
      sub.plan_type?.toLowerCase().includes(searchLower) ||
      sub.stripe_customer_id?.toLowerCase().includes(searchLower)
    );
    
    // Filter by status - show all subscriptions including active, trial, paid, etc.
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && (sub.status === 'active' || sub.status === 'trial' || sub.status === 'paid')) ||
      (statusFilter === 'inactive' && sub.status !== 'active' && sub.status !== 'trial' && sub.status !== 'paid');
    
    return matchesSearch && matchesStatus;
  });

  // Filter billing history based on search term
  const filteredBilling = billingHistory.filter(bill => {
    const searchLower = searchTerm.toLowerCase();
    return (
      bill.invoice_number?.toLowerCase().includes(searchLower) ||
      bill.description?.toLowerCase().includes(searchLower) ||
      bill.customer_id?.toLowerCase().includes(searchLower)
    );
  });

  // Separate payments (finalized) from drafts
  const filteredPayments = filteredBilling.filter(bill => bill.status !== 'draft');
  // Use draftInvoices from API instead of filtering billingHistory
  const filteredDrafts = draftInvoices.filter(draft => {
    const matchesSearch = !searchTerm || 
      (draft.invoice_number && draft.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (draft.description && draft.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (draft.customer?.name && draft.customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (draft.customer?.email && draft.customer.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  // Count totals for tab badges
  const totalPayments = billingHistory.filter(bill => bill.status !== 'draft').length;
  const totalDrafts = draftInvoices.length;
  
  // Filter subscription audit logs
  const filteredAuditLogs = subscriptionAuditLogs.filter(log => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (log.user_profiles?.email?.toLowerCase().includes(searchLower)) ||
      (getUserDisplayName(log.user_profiles || {}, '').toLowerCase().includes(searchLower)) ||
      (log.change_type?.toLowerCase().includes(searchLower)) ||
      (log.stripe_subscription_id?.toLowerCase().includes(searchLower)) ||
      (log.old_plan_type?.toLowerCase().includes(searchLower)) ||
      (log.new_plan_type?.toLowerCase().includes(searchLower));
    
    const matchesChangeType = changeTypeFilter === 'all' || log.change_type === changeTypeFilter;
    
    return matchesSearch && matchesChangeType;
  });


  const exportData = () => {
    const dataToExport = activeTab === 'subscriptions' 
      ? filteredSubscriptions 
      : activeTab === 'payments' 
        ? filteredPayments 
        : activeTab === 'drafts'
          ? filteredDrafts
          : filteredAuditLogs;
    const csvContent = activeTab === 'subscriptions'
      ? [
          ['User', 'Email', 'Plan', 'Status', 'Amount', 'Billing Cycle', 'Created'].join(','),
          ...dataToExport.map(sub => [
            getUserDisplayName(sub.user_profiles || {}, 'N/A'),
            sub.user_profiles?.email || 'N/A',
            sub.plan_type || 'N/A',
            sub.status || 'N/A',
            formatCurrency(sub.amount_cents),
            sub.billing_cycle || 'N/A',
            formatDate(sub.created_at)
          ].join(','))
        ].join('\n')
      : activeTab === 'subscription-log'
        ? [
            ['Date', 'User', 'Email', 'Change Type', 'Old Plan', 'New Plan', 'Old Status', 'New Status', 'Stripe Success', 'DB Updated', 'Webhook', 'Email Sent'].join(','),
            ...dataToExport.map(log => [
              formatDate(log.requested_at),
              getUserDisplayName(log.user_profiles || {}, 'N/A'),
              log.user_profiles?.email || 'N/A',
              log.change_type || 'N/A',
              log.old_plan_type || 'N/A',
              log.new_plan_type || 'N/A',
              log.old_status || 'N/A',
              log.new_status || 'N/A',
              log.stripe_api_success ? 'Yes' : 'No',
              log.database_updated ? 'Yes' : 'No',
              log.webhook_received ? 'Yes' : 'No',
              log.email_sent ? 'Yes' : 'No'
            ].join(','))
          ].join('\n')
        : [
            ['Invoice', 'Date', 'Amount', 'Status', 'Description'].join(','),
            ...dataToExport.map(bill => [
              bill.invoice_number || 'N/A',
              formatDate(bill.date),
              formatCurrency(bill.amount_cents),
              bill.status || 'N/A',
              bill.description || 'N/A'
            ].join(','))
          ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="w-full max-w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading payment data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-600 mt-2">Manage all platform subscriptions and billing</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportData}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={fetchData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <XCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'subscriptions'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard className="inline h-4 w-4 mr-2" />
              Subscriptions ({subscriptions.length})
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'payments'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircle className="inline h-4 w-4 mr-2" />
              Payments ({totalPayments})
            </button>
            <button
              onClick={() => setActiveTab('drafts')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'drafts'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              Drafts ({totalDrafts})
            </button>
            <button
              onClick={() => setActiveTab('subscription-log')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'subscription-log'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <History className="inline h-4 w-4 mr-2" />
              Subscription Log ({subscriptionAuditLogs.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center flex-1">
            <Search className="h-5 w-5 text-gray-400 mr-3" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-gray-700"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 ml-2"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {activeTab === 'subscriptions' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
          {activeTab === 'subscription-log' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Change Type:</label>
              <select
                value={changeTypeFilter}
                onChange={(e) => setChangeTypeFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="upgrade">Upgrade</option>
                <option value="downgrade">Downgrade</option>
                <option value="cancel">Cancel</option>
                <option value="reactivate">Reactivate</option>
                <option value="plan_change">Plan Change</option>
                <option value="billing_cycle_change">Billing Cycle Change</option>
                <option value="status_change">Status Change</option>
                <option value="created">Created</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              All Subscriptions ({filteredSubscriptions.length})
            </h3>
          </div>
          
          {filteredSubscriptions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'No subscriptions in the system yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSubscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(sub.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{sub.plan_type || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{sub.billing_cycle || 'monthly'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getUserDisplayName(sub.user_profiles || {}, 'Unknown User')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {sub.user_profiles?.email || sub.user_profiles?.username || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sub.stripe_subscription_id || sub.id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(sub.amount_cents)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(sub.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => setSelectedRecord(sub)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4 inline" />
                        </button>
                        {sub.status === 'active' && sub.stripe_subscription_id && (
                          <button
                            onClick={async () => {
                              if (window.confirm(`Cancel subscription for ${getUserDisplayName(sub.user_profiles || {}, 'this user')}? The subscription will remain active until the end of the billing period.`)) {
                                setCancellingSubscriptionId(sub.stripe_subscription_id);
                                setError('');
                                setMessage('');
                                try {
                                  const response = await stripeService.cancelSubscription(sub.stripe_subscription_id);
                                  if (response.success) {
                                    setMessage(`Subscription canceled successfully. Access will continue until end of billing period.`);
                                    setTimeout(() => {
                                      fetchData();
                                      setMessage('');
                                    }, 2000);
                                  } else {
                                    setError(response.error || 'Failed to cancel subscription');
                                  }
                                } catch (err) {
                                  setError(err.message || 'Failed to cancel subscription');
                                } finally {
                                  setCancellingSubscriptionId(null);
                                }
                              }
                            }}
                            disabled={cancellingSubscriptionId === sub.stripe_subscription_id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Cancel Subscription"
                          >
                            {cancellingSubscriptionId === sub.stripe_subscription_id ? (
                              <Loader2 className="h-4 w-4 inline animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4 inline" />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payments Tab (Finalized) */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Payments ({filteredPayments.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">Finalized payment transactions including subscriptions and one-off payments</p>
          </div>
          
          {filteredPayments.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No billing records found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'No billing history in the system yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((bill) => {
                    // Extract customer info
                    const customerName = bill.customer?.name 
                      || bill.customer_name
                      || (bill.user_profiles ? getUserDisplayName(bill.user_profiles, '') : '')
                      || 'N/A';
                    const customerEmail = bill.customer?.email 
                      || bill.customer_email
                      || bill.user_profiles?.email
                      || 'N/A';
                    
                    return (
                      <tr key={bill.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(bill.date || bill.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {bill.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {customerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {customerEmail}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {bill.invoice_number || bill.stripe_invoice_id || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(
                            bill.amount_cents || 
                            bill.total || 
                            bill.amount || 
                            bill.amount_due || 
                            bill.amount_paid || 
                            0
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            (bill.subscription_id || bill.subscription) 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {(bill.subscription_id || bill.subscription) ? 'Subscription' : 'One-Off'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(bill.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => setSelectedRecord(bill)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="h-4 w-4 inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Drafts Tab */}
      {activeTab === 'drafts' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Draft Invoices ({filteredDrafts.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">Invoices that have not yet been finalized</p>
          </div>
          
          {filteredDrafts.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No draft invoices</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'No draft invoices in the system'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDrafts.map((bill) => {
                    // Extract customer info from various possible sources
                    const customerName = bill.customer?.name 
                      || bill.customer_name
                      || (bill.user_profiles ? getUserDisplayName(bill.user_profiles, '') : '')
                      || 'N/A';
                    const customerEmail = bill.customer?.email 
                      || bill.customer_email
                      || bill.user_profiles?.email
                      || 'N/A';
                    
                    return (
                      <tr key={bill.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(bill.date || bill.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {bill.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {customerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {customerEmail}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {bill.invoice_number || bill.number || bill.id || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(bill.amount_cents || bill.total || bill.amount || bill.amount_due || bill.amount_paid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            (bill.subscription_id || bill.subscription)
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {(bill.subscription_id || bill.subscription) ? 'Subscription' : 'One-Off'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(bill.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => setSelectedRecord(bill)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4 inline" />
                          </button>
                          {(bill.id || bill.stripe_invoice_id) && (
                            <button
                              onClick={async () => {
                                const invoiceId = bill.id || bill.stripe_invoice_id;
                                if (!invoiceId) return;
                                setRefreshingInvoice(true);
                                try {
                                  const response = await stripeService.getInvoice(invoiceId);
                                  if (response.success && response.invoice) {
                                    setMessage('Invoice status synced from Stripe!');
                                    setTimeout(() => {
                                      fetchData();
                                      setMessage('');
                                    }, 1000);
                                  }
                                } catch (err) {
                                  setError('Failed to sync invoice status');
                                  setTimeout(() => setError(''), 3000);
                                } finally {
                                  setRefreshingInvoice(false);
                                }
                              }}
                              disabled={refreshingInvoice}
                              className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                              title="Sync status from Stripe"
                            >
                              <RefreshCw className={`h-4 w-4 inline ${refreshingInvoice ? 'animate-spin' : ''}`} />
                            </button>
                          )}
                          {bill.status === 'draft' && (bill.id || bill.stripe_invoice_id) && (
                          <button
                            onClick={async () => {
                              const invoiceId = bill.id || bill.stripe_invoice_id;
                              if (!invoiceId) return;
                              if (window.confirm('Finalize this invoice? It will be sent to the customer.')) {
                                setFinalizingInvoiceId(invoiceId);
                                setError('');
                                setMessage('');
                                try {
                                  const response = await stripeService.finalizeInvoice(invoiceId, { send_invoice: false });
                                  if (response.success) {
                                    setMessage('Invoice finalized successfully!');
                                    setTimeout(() => {
                                      fetchData();
                                      setMessage('');
                                    }, 2000);
                                  } else {
                                    setError(response.error || 'Failed to finalize invoice');
                                  }
                                } catch (err) {
                                  setError(err.message || 'Failed to finalize invoice');
                                } finally {
                                  setFinalizingInvoiceId(null);
                                }
                              }
                            }}
                            disabled={finalizingInvoiceId === (bill.id || bill.stripe_invoice_id)}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            title="Finalize Invoice"
                          >
                            {finalizingInvoiceId === (bill.id || bill.stripe_invoice_id) ? (
                              <Loader2 className="h-4 w-4 inline animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4 inline" />
                            )}
                          </button>
                        )}
                        {(bill.id || bill.stripe_invoice_id) && (
                          <button
                            onClick={() => setInvoiceToDelete(bill)}
                            className="text-red-600 hover:text-red-900"
                            title={bill.status === 'draft' ? 'Delete Invoice' : 'Void Invoice'}
                            disabled={deletingInvoiceId === (bill.id || bill.stripe_invoice_id)}
                          >
                            <Trash2 className="h-4 w-4 inline" />
                          </button>
                        )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Subscription Log Tab */}
      {activeTab === 'subscription-log' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Subscription Audit Log ({filteredAuditLogs.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Complete history of all subscription changes including upgrades, downgrades, and cancellations
            </p>
          </div>
          
          {filteredAuditLogs.length === 0 ? (
            <div className="p-8 text-center">
              <History className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || changeTypeFilter !== 'all' ? 'Try adjusting your filters' : 'No subscription changes logged yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Old Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      New Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Execution
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAuditLogs.map((log) => {
                    const userDisplayName = getUserDisplayName(log.user_profiles || {}, 'N/A');
                    const userEmail = log.user_profiles?.email || 'N/A';
                    
                    const getChangeTypeColor = (type) => {
                      const colors = {
                        'upgrade': 'bg-green-100 text-green-800',
                        'downgrade': 'bg-orange-100 text-orange-800',
                        'cancel': 'bg-red-100 text-red-800',
                        'reactivate': 'bg-blue-100 text-blue-800',
                        'plan_change': 'bg-purple-100 text-purple-800',
                        'billing_cycle_change': 'bg-yellow-100 text-yellow-800',
                        'status_change': 'bg-gray-100 text-gray-800',
                        'created': 'bg-indigo-100 text-indigo-800'
                      };
                      return colors[type] || 'bg-gray-100 text-gray-800';
                    };
                    
                    return (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>{formatDate(log.requested_at)}</div>
                          {log.effective_at && log.effective_at !== log.requested_at && (
                            <div className="text-xs text-gray-400">
                              Effective: {formatDate(log.effective_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{userDisplayName}</div>
                          <div className="text-sm text-gray-500">{userEmail}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getChangeTypeColor(log.change_type)}`}>
                            {log.change_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {log.initiated_by === 'user' ? '👤 User' : log.initiated_by === 'admin' ? '🛡️ Admin' : '🤖 System'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.old_plan_type ? (
                            <div>
                              <div>{log.old_plan_type}</div>
                              {log.old_amount_cents && (
                                <div className="text-xs text-gray-500">{formatCurrency(log.old_amount_cents)}</div>
                              )}
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.new_plan_type ? (
                            <div>
                              <div>{log.new_plan_type}</div>
                              {log.new_amount_cents && (
                                <div className="text-xs text-gray-500">{formatCurrency(log.new_amount_cents)}</div>
                              )}
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col gap-1">
                            {log.old_status && log.new_status && log.old_status !== log.new_status && (
                              <div>
                                <span className="text-gray-500">{log.old_status}</span>
                                <span className="mx-1">→</span>
                                <span className="font-medium">{log.new_status}</span>
                              </div>
                            )}
                            {log.old_expires_at && log.change_type === 'cancel' && (
                              <div className="text-xs text-gray-500">
                                Ends: {formatDate(log.old_expires_at)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {log.stripe_api_success ? (
                                <CheckCircle className="h-4 w-4 text-green-500" title="Stripe API Success" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" title="Stripe API Failed" />
                              )}
                              {log.database_updated ? (
                                <CheckCircle className="h-4 w-4 text-green-500" title="Database Updated" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" title="Database Update Failed" />
                              )}
                              {log.webhook_received ? (
                                <CheckCircle className="h-4 w-4 text-green-500" title="Webhook Received" />
                              ) : log.initiated_by === 'system' ? (
                                <Clock className="h-4 w-4 text-yellow-500" title="Webhook Pending" />
                              ) : null}
                              {log.email_sent ? (
                                <CheckCircle className="h-4 w-4 text-blue-500" title="Email Sent" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400" title="Email Not Sent" />
                              )}
                            </div>
                            {log.error_message && (
                              <div className="text-xs text-red-600 truncate max-w-xs" title={log.error_message}>
                                {log.error_message}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setSelectedRecord(log)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="h-4 w-4 inline mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {/* Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {activeTab === 'subscriptions' ? 'Subscription Details' : 
                   activeTab === 'subscription-log' ? 'Subscription Change Details' :
                   'Invoice Details'}
                </h3>
                <div className="flex items-center gap-2">
                  {(activeTab === 'payments' || activeTab === 'drafts') && selectedRecord?.stripe_invoice_id && (
                    <button
                      onClick={async () => {
                        if (!selectedRecord.stripe_invoice_id) return;
                        setRefreshingInvoice(true);
                        try {
                          const response = await stripeService.getInvoice(selectedRecord.stripe_invoice_id);
                          if (response.success && response.invoice) {
                            // Merge the fresh data with existing record
                            setSelectedRecord({
                              ...selectedRecord,
                              ...response.invoice,
                              status: response.invoice.status,
                              hosted_invoice_url: response.invoice.hosted_invoice_url || selectedRecord.hosted_invoice_url,
                              total: response.invoice.total || response.invoice.amount_due,
                              amount_due: response.invoice.amount_due,
                              currency: response.invoice.currency
                            });
                            setMessage('Invoice data refreshed successfully!');
                            setTimeout(() => setMessage(''), 3000);
                          }
                        } catch (err) {
                          console.error('Error refreshing invoice:', err);
                          setError('Failed to refresh invoice data');
                          setTimeout(() => setError(''), 3000);
                        } finally {
                          setRefreshingInvoice(false);
                        }
                      }}
                      disabled={refreshingInvoice}
                      className="text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      title="Refresh invoice data from Stripe"
                    >
                      <RefreshCw className={`h-5 w-5 ${refreshingInvoice ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedRecord(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <div className="max-h-[600px] overflow-auto">
                {selectedRecord ? (
                  activeTab === 'subscription-log' ? (
                    // Subscription Log Details
                    <div className="space-y-4">
                      {/* Change Type Banner */}
                      <div className="bg-gray-50 border-2 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Change Type</p>
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                              selectedRecord.change_type === 'upgrade' ? 'bg-green-100 text-green-800' :
                              selectedRecord.change_type === 'downgrade' ? 'bg-orange-100 text-orange-800' :
                              selectedRecord.change_type === 'cancel' ? 'bg-red-100 text-red-800' :
                              selectedRecord.change_type === 'reactivate' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {selectedRecord.change_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Initiated By</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedRecord.initiated_by === 'user' ? '👤 User' : 
                               selectedRecord.initiated_by === 'admin' ? '🛡️ Admin' : 
                               '🤖 System'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Timestamps */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Requested At</p>
                          <p className="text-sm font-mono text-blue-900">{formatDate(selectedRecord.requested_at)}</p>
                        </div>
                        {selectedRecord.effective_at && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-1">Effective At</p>
                            <p className="text-sm font-mono text-green-900">{formatDate(selectedRecord.effective_at)}</p>
                          </div>
                        )}
                      </div>

                      {/* Plan Changes */}
                      {(selectedRecord.old_plan_type || selectedRecord.new_plan_type) && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">Plan Changes</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Old Plan</p>
                              <p className="text-sm font-semibold text-gray-900">{selectedRecord.old_plan_type || 'N/A'}</p>
                              {selectedRecord.old_amount_cents && (
                                <p className="text-xs text-gray-500 mt-1">{formatCurrency(selectedRecord.old_amount_cents)}</p>
                              )}
                              {selectedRecord.old_billing_cycle && (
                                <p className="text-xs text-gray-500">{selectedRecord.old_billing_cycle}</p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">New Plan</p>
                              <p className="text-sm font-semibold text-gray-900">{selectedRecord.new_plan_type || 'N/A'}</p>
                              {selectedRecord.new_amount_cents && (
                                <p className="text-xs text-gray-500 mt-1">{formatCurrency(selectedRecord.new_amount_cents)}</p>
                              )}
                              {selectedRecord.new_billing_cycle && (
                                <p className="text-xs text-gray-500">{selectedRecord.new_billing_cycle}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Status Changes */}
                      {(selectedRecord.old_status || selectedRecord.new_status) && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">Status Changes</p>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{selectedRecord.old_status || 'N/A'}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-sm font-semibold text-gray-900">{selectedRecord.new_status || 'N/A'}</span>
                          </div>
                          {selectedRecord.old_expires_at && selectedRecord.change_type === 'cancel' && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-500 mb-1">Subscription End Date (at cancellation)</p>
                              <p className="text-sm font-semibold text-red-600">{formatDate(selectedRecord.old_expires_at)}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Execution Status */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Execution Status</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            {selectedRecord.stripe_api_success ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                            <span className="text-sm text-gray-700">Stripe API</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedRecord.database_updated ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                            <span className="text-sm text-gray-700">Database</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedRecord.webhook_received ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : selectedRecord.initiated_by === 'system' ? (
                              <Clock className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-700">Webhook</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedRecord.email_sent ? (
                              <CheckCircle className="h-5 w-5 text-blue-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-700">Email Sent</span>
                          </div>
                        </div>
                        {selectedRecord.error_message && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-red-600 mb-1">Error Message</p>
                            <p className="text-sm text-red-700">{selectedRecord.error_message}</p>
                          </div>
                        )}
                        {selectedRecord.stripe_error_code && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-red-600 mb-1">Stripe Error Code</p>
                            <p className="text-sm text-red-700 font-mono">{selectedRecord.stripe_error_code}</p>
                          </div>
                        )}
                      </div>

                      {/* User Info */}
                      {selectedRecord.user_profiles && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">User Information</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Name</p>
                              <p className="text-sm text-gray-900">{getUserDisplayName(selectedRecord.user_profiles || {}, 'N/A')}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Email</p>
                              <p className="text-sm text-gray-900">{selectedRecord.user_profiles?.email || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Subscription IDs */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Subscription IDs</p>
                        <div className="space-y-1">
                          {selectedRecord.stripe_subscription_id && (
                            <p className="text-xs font-mono text-gray-700 break-all">
                              <span className="font-medium">Stripe:</span> {selectedRecord.stripe_subscription_id}
                            </p>
                          )}
                          {selectedRecord.stripe_customer_id && (
                            <p className="text-xs font-mono text-gray-700 break-all">
                              <span className="font-medium">Customer:</span> {selectedRecord.stripe_customer_id}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Metadata */}
                      {selectedRecord.metadata && Object.keys(selectedRecord.metadata).length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Additional Metadata</p>
                          <pre className="text-xs text-gray-700 overflow-auto max-h-32">
                            {JSON.stringify(selectedRecord.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Formatted invoice preview (existing code)
                    <div className="space-y-4">
                      {/* For subscriptions, try to fetch latest invoice */}
                      {activeTab === 'subscriptions' && selectedRecord.stripe_subscription_id && !selectedRecord.hosted_invoice_url && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-blue-800">
                            <strong>Note:</strong> To view invoice details, check the Payments tab for invoices related to this subscription.
                          </p>
                        </div>
                      )}
                      {/* Status Banner - Most Prominent */}
                      <div className="bg-gray-50 border-2 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Invoice Status</p>
                            <div>
                              {getStatusBadge(selectedRecord.status || 'draft', 'large')}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Total Amount</p>
                            <p className="text-2xl font-bold text-gray-900">
                              ${((selectedRecord.total || selectedRecord.amount_due || selectedRecord.amount_cents || 0) / 100).toFixed(2)}
                              {selectedRecord.currency && selectedRecord.currency !== 'usd' && (
                                <span className="text-sm font-normal text-gray-600 ml-1">{selectedRecord.currency.toUpperCase()}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                    {/* Invoice ID - Full ID visible */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Invoice ID</p>
                      <p className="text-sm font-mono text-blue-900 break-all">{selectedRecord.stripe_invoice_id || selectedRecord.id || 'N/A'}</p>
                    </div>

                    {/* Invoice Details Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Customer Name</p>
                        <p className="text-sm text-gray-900">
                          {selectedRecord.customer?.name || 
                           selectedRecord.metadata?.customer_name || 
                           getUserDisplayName(selectedRecord.user_profiles || {}, 'N/A')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Customer Email</p>
                        <p className="text-sm text-gray-900">
                          {selectedRecord.customer?.email || 
                           selectedRecord.metadata?.customer_email || 
                           selectedRecord.user_profiles?.email || 
                           'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Creation Date</p>
                        <p className="text-sm text-gray-900">{formatDate(selectedRecord.date || selectedRecord.created_at || selectedRecord.created)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Currency</p>
                        <p className="text-sm text-gray-900 uppercase">{selectedRecord.currency || 'USD'}</p>
                      </div>
                    </div>
                    {selectedRecord.description && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Description</p>
                        <p className="text-sm text-gray-900">{selectedRecord.description}</p>
                      </div>
                    )}
                    {(selectedRecord.line_items && selectedRecord.line_items.length > 0) || (selectedRecord.metadata?.line_items && selectedRecord.metadata.line_items.length > 0) ? (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Line Items</p>
                        <div className="border border-gray-200 rounded-md overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {(selectedRecord.line_items || selectedRecord.metadata?.line_items || []).map((item, idx) => {
                                const quantity = item.quantity || 1;
                                const unitAmount = item.unit_amount || item.amount || 0;
                                const total = unitAmount * quantity;
                                return (
                                  <tr key={idx}>
                                    <td className="px-3 py-2 text-gray-900">{item.description || 'N/A'}</td>
                                    <td className="px-3 py-2 text-right text-gray-900">{quantity}</td>
                                    <td className="px-3 py-2 text-right text-gray-900">
                                      ${(unitAmount / 100).toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                      ${(total / 100).toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Line Items</p>
                        <p className="text-sm text-gray-400">No line items available.</p>
                      </div>
                    )}
                    {/* Invoice URL Section - Always visible if available */}
                    {(selectedRecord.hosted_invoice_url || selectedRecord.metadata?.hosted_invoice_url) && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Invoice Payment URL
                        </p>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            readOnly
                            value={selectedRecord.hosted_invoice_url || selectedRecord.metadata?.hosted_invoice_url}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white font-mono text-xs"
                            onClick={(e) => e.target.select()}
                          />
                          <button
                            onClick={() => {
                              const url = selectedRecord.hosted_invoice_url || selectedRecord.metadata?.hosted_invoice_url;
                              navigator.clipboard.writeText(url);
                              setMessage('Invoice URL copied to clipboard!');
                              setTimeout(() => setMessage(''), 2000);
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center whitespace-nowrap"
                            title="Copy URL"
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </button>
                          <a
                            href={selectedRecord.hosted_invoice_url || selectedRecord.metadata?.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center whitespace-nowrap"
                            title="Open invoice in new tab"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Open
                          </a>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          Share this URL with the customer to allow them to pay the invoice.
                        </p>
                      </div>
                    )}
                    
                    {/* Status-specific messages */}
                    {!selectedRecord.hosted_invoice_url && !selectedRecord.metadata?.hosted_invoice_url && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-800">
                          {selectedRecord.status === 'draft' ? (
                            <>⚠️ <strong>Draft Invoice:</strong> Payment URL will be available after finalization. Go to the Drafts tab to finalize this invoice.</>
                          ) : (
                            <>ℹ️ Invoice URL not available. This may be a draft invoice that needs to be finalized.</>
                          )}
                        </p>
                      </div>
                    )}
                    </div>
                  )
                ) : null}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {invoiceToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {invoiceToDelete.status === 'draft' ? 'Delete Invoice' : 'Void Invoice'}
                </h3>
                <button
                  onClick={() => {
                    setInvoiceToDelete(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={deletingInvoiceId === (invoiceToDelete.id || invoiceToDelete.stripe_invoice_id)}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to {invoiceToDelete.status === 'draft' ? 'delete' : 'void'} this invoice?
                </p>
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-500 mb-1">Invoice</p>
                  <p className="text-sm font-medium text-gray-900">
                    {invoiceToDelete.invoice_number || invoiceToDelete.number || invoiceToDelete.id || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 mb-1">Amount</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(invoiceToDelete.amount_cents || invoiceToDelete.total || invoiceToDelete.amount || invoiceToDelete.amount_due || 0)}
                  </p>
                  {invoiceToDelete.description && (
                    <>
                      <p className="text-xs text-gray-500 mt-2 mb-1">Description</p>
                      <p className="text-sm text-gray-900">{invoiceToDelete.description}</p>
                    </>
                  )}
                </div>
                {invoiceToDelete.status !== 'draft' && (
                  <p className="mt-3 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                    Note: This will void the invoice. It cannot be undone and the invoice will be marked as voided.
                  </p>
                )}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setInvoiceToDelete(null);
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  disabled={deletingInvoiceId === (invoiceToDelete.id || invoiceToDelete.stripe_invoice_id)}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const invoiceId = invoiceToDelete.id || invoiceToDelete.stripe_invoice_id;
                    if (!invoiceId) return;
                    
                    setDeletingInvoiceId(invoiceId);
                    setError('');
                    setMessage('');

                    try {
                      const deleteResponse = await stripeService.deleteInvoice(invoiceId);
                      
                      if (!deleteResponse.success) {
                        throw new Error(deleteResponse.error || 'Failed to delete invoice');
                      }

                      setMessage(deleteResponse.message || `Invoice ${deleteResponse.action || 'deleted'} successfully!`);
                      
                      // Close modal first
                      setInvoiceToDelete(null);
                      
                      // Refresh data
                      setTimeout(async () => {
                        await fetchData();
                        setMessage('');
                      }, 1000);

                    } catch (err) {
                      console.error('Error deleting invoice:', err);
                      setError(err.message || 'Failed to delete invoice');
                    } finally {
                      setDeletingInvoiceId(null);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={deletingInvoiceId === (invoiceToDelete.id || invoiceToDelete.stripe_invoice_id)}
                >
                  {deletingInvoiceId === (invoiceToDelete.id || invoiceToDelete.stripe_invoice_id) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {invoiceToDelete.status === 'draft' ? 'Deleting...' : 'Voiding...'}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {invoiceToDelete.status === 'draft' ? 'Delete' : 'Void'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;

